<script setup>
import { computed, nextTick, onBeforeMount, ref } from 'vue';
import AppIcon from 'module/icon/AppIcon.vue';
import {
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogOverlay,
    DialogPortal,
    DialogRoot,
    DialogTitle,
    VisuallyHidden,
} from 'reka-ui';
import { user } from 'app/user';
import { useEventListener } from '@vueuse/core';
import { ENDPOINT_USER_LOGIN, ENDPOINT_USER_REGISTER } from 'app/endpoints';
import { useBreakpoints } from '../../composables/breakpoints';
import { useLogin } from '../../composables/login';

const { redirectUrl, loginModalActive, showLoginModal, mode } = useLogin();

const endpoint = computed(() => {
    if (mode.value === 'login') return ENDPOINT_USER_LOGIN;
    if (mode.value === 'register') return ENDPOINT_USER_REGISTER;
    throw new Error(`Unsupported modal mode '${mode.value}'`);
});

const iframeSrc = computed(() => {
    const url = new URL(endpoint.value, window?.location.origin);
    url.searchParams.set('redirectUrl', redirectUrl.value);
    url.searchParams.set('isModal', '1');
    return url.href;
});

const emit = defineEmits(['showSnackbar']);

async function reopen() {
    if (loginModalActive.value) {
        loginModalActive.value = false;
        await nextTick();
        showLoginModal();
    }
}

// TODO refactor to computed / state-driven instead of this imperative clusterfuck
const contentInnerRef = ref(null);
function gdprModalIsOpen() {
    if (!contentInnerRef.value) return false;
    return contentInnerRef.value.classList.contains('m-loginModal__contentInner--gdpr');
}

const { isMobile } = useBreakpoints();

const iFrameRef = ref(null);
const iFrameContainerRef = ref(null);
const sceneRef = ref(null);
const boxRef = ref(null);
const containerRef = ref(null);
function receiveMessageFromSSO(event) {
    // The SSO GDPR modal has a fixed-positioned layout, so we can't get valid height for it.
    if (event.data.type === 'event'
        && event.data.action === 'updateHeight'
        && !gdprModalIsOpen()
        && iFrameRef.value) {
        iFrameContainerRef.value.style.height = `${event.data.value}px`;
    }

    if (event.data.type === 'event' && event.data.action === 'openGdprModal') {
        if (containerRef.value) containerRef.value.style.width = '100%';
        sceneRef.value?.setAttribute('style', 'display: none;');
        contentInnerRef.value?.classList.add('m-loginModal__contentInner--gdpr');
        boxRef.value?.classList.add('m-loginModal__box--gdpr');
        iFrameContainerRef.value?.classList.add('m-loginModal__iFrameContainer--gdpr');

        if (isMobile.value && contentInnerRef.value) {
            contentInnerRef.value.scrollTop = 0;
        } else if (containerRef.value) {
            containerRef.value.scrollTop = 0;
        }

        iFrameRef.value?.contentWindow.postMessage({
            type: 'event',
            action: 'gdprChanged',
        }, '*');
    }

    if (event.data.type === 'event' && event.data.action === 'closeGdprModal') {
        if (isMobile.value && containerRef.value) {
            containerRef.value.style.width = '100%';
            contentInnerRef.value.scrollTop = 0;
        } else if (containerRef.value) {
            containerRef.value.style.width = '60%';
            containerRef.value.scrollTop = 0;
        }
        sceneRef.value?.setAttribute('style', 'display: flex;');
        contentInnerRef.value?.classList.remove('m-loginModal__contentInner--gdpr');
        boxRef.value.classList?.remove('m-loginModal__box--gdpr');
        iFrameContainerRef.value?.classList.remove('m-loginModal__iFrameContainer--gdpr');

        iFrameRef.value?.contentWindow.postMessage({
            type: 'event',
            action: 'gdprChanged',
        }, '*');
    }

    // Triggers if you resend the confirm mail after register
    if (event.data.type === 'event' && event.data.action === 'showSnackbar') {
        emit('showSnackbar', event.data.label);
    }

    // Triggers if a parallel login-flow is started in another tab
    if (event.data.type === 'event' && event.data.action === 'loginExpired') {
        // Since this event can only be triggered from another tab, this window-instance isn't visible.
        // As soon as the user switches back to this tab and if the user didn't log in in the meantime,
        // we will reopen the login-modal in order to start a new login-flow.
        // If the user is already logged-in and logs in again, the `RedirectIfAuthenticated`-middleware
        // will redirect the user to the requested location, so we don't need to intervene in this case.
        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'visible' && !user.isLoggedIn(true)) {
                reopen();
            }
        }, { once: true });
    }
}

onBeforeMount(() => {
    useEventListener(window, 'message', receiveMessageFromSSO);
});
</script>

<template>
    <DialogRoot
        v-model:open="loginModalActive"
        class="m-loginModal"
    >
        <DialogPortal>
            <DialogOverlay class="m-loginModal__overlay m-loginModal__overlay--animate" />
            <DialogContent>
                <div class="m-loginModal__content">
                    <VisuallyHidden as-child>
                        <DialogTitle>
                            Anmelden und loslegen!
                        </DialogTitle>
                    </VisuallyHidden>
                    <VisuallyHidden as-child>
                        <DialogDescription>
                            Melde dich mit deinem karriere.at Konto an, oder registriere dich.
                        </DialogDescription>
                    </VisuallyHidden>
                    <div
                        ref="contentInnerRef"
                        class="m-loginModal__contentInner m-loginModal__contentInner--animate"
                    >
                        <div
                            ref="boxRef"
                            class="m-loginModal__box"
                        >
                            <div
                                ref="sceneRef"
                                class="m-loginModal__scene"
                            >
                                <div class="m-loginModal__sceneImageContainer">
                                    <span class="m-loginModal__bubble">Potential entfalten</span>
                                    <img
                                        src="/assets/images/auraPose.png"
                                        loading="lazy"
                                        alt="Illustration einer geöffneten Tür, die symbolisieren soll, dass viele
                                        Chancen auf einen warten nachdem man den Bereich nach dem Login betreten hat."
                                        class="m-loginModal__image"
                                    >
                                    <ul class="m-loginModal__list">
                                        <li class="m-loginModal__listItem">
                                            <AppIcon
                                                name="check"
                                                class="k-c-list__icon"
                                                size="medium"
                                            />
                                            <span>
                                                Strukturiert und übersichtlich deinen Lebenslauf online erstellen.
                                            </span>
                                        </li>
                                        <li class="m-loginModal__listItem">
                                            <AppIcon
                                                name="check"
                                                class="k-c-list__icon"
                                                size="medium"
                                            />
                                            <span>
                                                Smart bewerben: Schnell und un-kompliziert mit einem Klick bewerben.
                                            </span>
                                        </li>
                                        <li class="m-loginModal__listItem">
                                            <AppIcon
                                                name="check"
                                                class="k-c-list__icon"
                                                size="medium"
                                            />
                                            <span>
                                                Zeit sparen und Anfragen von Arbeit-gebern erhalten.
                                            </span>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                            <div
                                ref="containerRef"
                                class="m-loginModal__container"
                            >
                                <div
                                    ref="iFrameContainerRef"
                                    class="m-loginModal__iFrameContainer"
                                >
                                    <DialogClose
                                        class="m-loginModal__close k-c-modal__closeIcon"
                                        aria-label="Login Fenster schließen"
                                    >
                                        <AppIcon
                                            name="cross"
                                            size="small"
                                        />
                                    </DialogClose>
                                    <iframe
                                        ref="iFrameRef"
                                        class="m-loginModal__iFrame"
                                        :src="iframeSrc"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </DialogContent>
        </DialogPortal>
    </DialogRoot>
</template>

<style lang="scss">
@use "styles";
</style>
